/**
 * The window's file-drop gesture, over the DOM — shared by every build whose
 * host lets the drag reach the page (the browser's and Electron's). Those two
 * differ only in what a dropped `File` *resolves to*, so only that is left to
 * them and the gesture itself lives here once.
 *
 * Bound on `window` rather than on any one component, so a drop lands the same
 * way whatever view is on screen; DropArea owns the subscription and routes
 * the files to the mounted view.
 *
 * `capture` is not a detail, it is what makes this embeddable. A host page has
 * global drag handlers of its own (the sprint24 frontend delegates `dragover`
 * and `drop` on `document`, and for staff the latter swallows the files into
 * the file storage), and `document` sits below `window` on the way up — so a
 * bubbling listener here would see the drop only after the page had already
 * taken it. Capturing runs first instead, and `stopPropagation` then keeps the
 * gesture off the page entirely: while a DropArea is mounted it owns the
 * window's file drops, and nothing is suppressed once it unmounts.
 *
 * Returns its own teardown.
 */
export const WindowFileDrop = (handlers: {
  /** A drag entered (true) or left (false) the window. */
  on_dragging: (dragging: boolean) => void
  /** A non-empty drop. Never called with zero files. */
  on_files: (files: File[]) => void
}): (() => void) => {
  const { on_dragging, on_files } = handlers

  // Only a drag carrying files from outside the page is ours. The DOM fires
  // these on `window` for *every* drag, in-page ones included (the file rows
  // are reordered by dragging — see FileRows), and without this the whole
  // window lit up as a drop target the moment a row was picked up. It is also
  // what keeps the capture above from stealing those in-page drags from the
  // rows that own them.
  const has_files = (e: DragEvent) => !!e.dataTransfer?.types.includes("Files")

  // Both dragover and drop need preventDefault: without the first the browser
  // refuses the drop and navigates to the file instead.
  const on_dragover = (e: DragEvent) => {
    if (!has_files(e)) return
    e.preventDefault()
    e.stopPropagation()
    on_dragging(true)
  }
  // Only a drag leaving the window itself counts — moving between elements
  // fires dragleave with the element being entered as `relatedTarget`.
  const on_dragleave = (e: DragEvent) => {
    if (!e.relatedTarget) on_dragging(false)
  }
  const on_drop = (e: DragEvent) => {
    if (!has_files(e)) return
    e.preventDefault()
    e.stopPropagation()
    on_dragging(false)
    const dropped = [...(e.dataTransfer?.files ?? [])]
    if (dropped.length) on_files(dropped)
  }

  window.addEventListener("dragover", on_dragover, true)
  window.addEventListener("dragleave", on_dragleave, true)
  window.addEventListener("drop", on_drop, true)
  return () => {
    window.removeEventListener("dragover", on_dragover, true)
    window.removeEventListener("dragleave", on_dragleave, true)
    window.removeEventListener("drop", on_drop, true)
  }
}
